import { Badge, Button, Card, Col, Collapse, Divider, Empty, Progress, Row, Spin, Statistic } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CorridorInfo from "./CorridorInfo";
import SupervisionService from "../../../../../services/SupervisionService";
import Supervision, { SupervisionWarehouse } from "../../../../../models/Supervision";
import { CaretLeftOutlined, InfoCircleOutlined, VideoCameraTwoTone } from "@ant-design/icons";
import { PalletDevicePendingCount } from "../../../../../models/PalletDevice";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useLanguage } from "../../../../../context/LanguageContext";
import CorridorsControls from "./CorridorsControls";
import CorridorSelectorMap from "./CorridorSelectorMap";
import CorridorMap from "./details/CorridorMap";
import { SupervisionDetails } from "../../../../../models/Supervision";

const Corridor: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const { warehouseIdx } = useParams();
  const [supervisionWarehouse, setSupervisionWarehouse] = useState<SupervisionWarehouse>();
  const [supervisions, setsupervisions] = useState<Supervision[]>([]);
  const [parentSizes, setParentSizes] = useState<Record<number, { width: number, height: number }>>({});
  const [corridorsLoading, setCorridorsLoading] = useState<boolean>(true)
  const [devicesPendingCount, setDevicesPendingCount] = useState<PalletDevicePendingCount[]>([]);
  const navigate = useNavigate()
  const screens = useBreakpoint();
  const { language } = useLanguage();
  const corridorDetailsCardBodyStyle = useMemo<React.CSSProperties>(
    () => ({
      height: screens.xs ? 320 : 190,
      overflow: "auto",
    }),
    [screens.xs]
  );
  const [corridorFilter, setCorridorFilter] = useState<string>("");
  const [deviceFilter, setDeviceFilter] = useState<string[]>([]);
  const [toShowFilter, setToShowFilter] = useState<number>(1);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedCorridorName, setSelectedCorridorName] = useState<string | null>(null);
  const [selectedCorridorDetails, setSelectedCorridorDetails] = useState<SupervisionDetails>();
  const [corridorMapLoading, setCorridorMapLoading] = useState<boolean>(false);
  
  const { Panel } = Collapse;

  const getCorridorSeverity = useCallback((supervision: Supervision) => {
    if (supervision.reading_percentage < 0.1) {
      return {
        color: "gold",
        strokeColor: "#fadb14",
      };
    }

    if (supervision.effective_percentage < 0.5) {
      return {
        color: "red",
        strokeColor: "#f5222d",
      };
    }

    if (supervision.effective_percentage < 0.75) {
      return {
        color: "gold",
        strokeColor: "#fadb14",
      };
    }

    return {
      color: "green",
      strokeColor: "#52c41a",
    };
  }, [language]);

  const handleExpandAll = () => {
    const keys = filteredSupervisions.map((supervision) => supervision.corridor_name);
    setActiveKeys(keys);
  };

  const handleCollapseAll = () => {
    setActiveKeys([]);
  };


  const onApplyFilters = useCallback(
       (
         device: string[],
         corridor: string,
         toShowFilter: number
       ) => {
         setDeviceFilter(device)
         setCorridorFilter(corridor);
         setToShowFilter(toShowFilter);
       },
       []
     );

     const filteredByDeviceAndState = useMemo(() => {
      let filtered = supervisions;

      if (deviceFilter.length > 0) {
        filtered = filtered.filter((item) =>
          deviceFilter.includes(item.device_name)
        );
      }

      if (toShowFilter !== 1) {
        if (toShowFilter !== 3) {
          filtered = filtered.filter((entry) => entry.reading_percentage > 0.1);
        } else {
          filtered = filtered.filter((entry) => entry.reading_percentage < 0.1);
        }
      }

      return filtered;
     }, [supervisions, deviceFilter, toShowFilter]);

     const filteredSupervisions = useMemo(() => {
       let filtered = filteredByDeviceAndState;

       if (corridorFilter !== "") {
         filtered = filtered.filter((entry) =>
           entry.corridor_name
             .toLocaleUpperCase()
             .includes(corridorFilter.toLocaleUpperCase())
   
         );
       }
       if (selectedCorridorName !== null) {
        filtered = filtered.filter((entry) => entry.corridor_name === selectedCorridorName);
       }

       return filtered;
     }, [
       filteredByDeviceAndState,
       corridorFilter,
       selectedCorridorName
     ]);

  const selectedSupervision = useMemo(() => {
    if (selectedCorridorName === null) {
      return undefined;
    }

    return filteredByDeviceAndState.find(
      (entry) => entry.corridor_name === selectedCorridorName
    );
  }, [filteredByDeviceAndState, selectedCorridorName]);

  useEffect(() => {
    if (selectedCorridorName === null) {
      return;
    }

    const existsInCurrentData = filteredByDeviceAndState.some(
      (entry) => entry.corridor_name === selectedCorridorName
    );

    if (!existsInCurrentData) {
      setSelectedCorridorName(null);
    }
  }, [filteredByDeviceAndState, selectedCorridorName]);

  useEffect(() => {
    if (
      selectedSupervision === undefined ||
      selectedSupervision.corridor_idx === undefined ||
      supervisionWarehouse?.sweep_idx === undefined ||
      warehouseIdx === undefined
    ) {
      setSelectedCorridorDetails(undefined);
      return;
    }

    setCorridorMapLoading(true);

    SupervisionService.getDetails(
      parseInt(warehouseIdx),
      selectedSupervision.corridor_idx,
      navigate
    )
      .then((details) => {
        setSelectedCorridorDetails(details || undefined);
      })
      .catch((error) => {
        console.error("Failed to fetch selected corridor details:", error);
        setSelectedCorridorDetails(undefined);
      })
      .finally(() => {
        setCorridorMapLoading(false);
      });
  }, [selectedSupervision, supervisionWarehouse?.sweep_idx, warehouseIdx, navigate]);

  const updateContent = useCallback(() => {
    setLoading(true)
    if (warehouseIdx === undefined) return;
    SupervisionService.getAll(parseInt(warehouseIdx), navigate)
      .then((supervisions) => {
        setsupervisions(supervisions || []);
        setCorridorsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch supervisions:", error);
        setsupervisions([]);
        setCorridorsLoading(false);
      });
    SupervisionService.getSupervisionWarehouse(parseInt(warehouseIdx), navigate)
      .then((supervisionWarehouse) => {
        setSupervisionWarehouse(supervisionWarehouse || undefined);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch warehouses:", error);
        setLoading(false);
      });
    SupervisionService.getSupervisionPendingCount(parseInt(warehouseIdx), navigate)
      .then((pendingCount) => {
        if (pendingCount != null) {
          setDevicesPendingCount(pendingCount || []);
        }
      })
  }, [warehouseIdx, navigate]);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 300000);
    return () => clearInterval(interval);
  }, [updateContent]);

  const handleResize = (idx: number, entry: ResizeObserverEntry) => {
    setParentSizes((prevSizes) => ({
      ...prevSizes,
      [idx]: {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      },
    }));
  };

  return (
    <Card>
      <Spin spinning={loading}>
        {supervisionWarehouse && <Card
          title={supervisionWarehouse.warehouse_name}
          extra={<Link to={`/supervision`} >
            <Button
              type="primary"
              shape="round"
              icon={<CaretLeftOutlined />}
            >
              {language === "es" ? "Atrás" : "Back"}
            </Button>
          </Link>}>
          {supervisionWarehouse.is_processing &&
            <Row gutter={[10, 10]}>
              {selectedSupervision ? (
                <>
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Card
                      title={language === "es" ? `Rack ${selectedSupervision.corridor_name}` : `Corridor ${selectedSupervision.corridor_name}`}
                      style={{ height: "100%", textAlign: "center" }}
                    >
                      <Row gutter={[8, 10]}>
                        <Col span={24} style={{ textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 7,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <b style={{ fontSize: screens.lg ? "0.90vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{language === "es" ? "Dispositivo:" : "Device:"}</b>
                            <span style={{ fontSize: screens.lg ? "0.90vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw", fontWeight: 500 }}>{selectedSupervision.device_name || "-"}</span>
                            <VideoCameraTwoTone twoToneColor="#ff4d4f" />
                          </span>
                        </Col>
                        {/* <Col span={24} style={{ textAlign: "center" }}>
                          <b>{language === "es" ? "Batería" : "Battery"}</b>
                        </Col>
                        <Col span={24}>
                          <Progress
                            percent={parseFloat((selectedSupervision.battery_level * 100).toFixed(2))}
                            showInfo
                            strokeLinecap="butt"
                            steps={10}
                            strokeColor={
                              selectedSupervision.battery_level > 0.7
                                ? "#52c41a"
                                : selectedSupervision.battery_level > 0.3
                                  ? "#fadb14"
                                  : "#f5222d"
                            }
                          />
                        </Col> */}
                      </Row>
                    </Card>
                  </Col>
                  <Col span={screens.xs ? 24 : 8} style={{ textAlign: "center" }}>
                    <Card title={language === "es" ? "Lecturas" : "Readings"}>
                      <Row gutter={[10, 15]}>
                        <Col span={24}>
                          <Progress
                            type="line"
                            percent={parseFloat((selectedSupervision.reading_percentage * 100).toFixed(2))}
                            format={(percent) => `${percent}%`}
                            strokeWidth={10} // grosor de la barra en píxeles
                            status="active"  // añade una animación sutil de carga (opcional)
                          />
                        </Col>
                        <Col span={23}>
                          <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${selectedSupervision.read_positions} / ${selectedSupervision.total_count}`}</b>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  <Col span={screens.xs ? 24 : 8} style={{ textAlign: "center" }}>
                    <Card title={language === "es" ? "Efectividad" : "Effective"}>
                      <Row gutter={[10, 15]}>
                        <Col span={23}>
                          <Progress
                            type="line"
                            percent={parseFloat((selectedSupervision.effective_percentage * 100).toFixed(2))}
                            format={(percent) => `${percent}%`}
                            strokeWidth={10} // grosor de la barra en píxeles
                            status="active"  // añade una animación sutil de carga (opcional)
                            strokeColor={selectedSupervision.effective_percentage > 0.75 ? "#1677ff" : selectedSupervision.effective_percentage > 0.50 ? "#fadb14" : "#f5222d"}
                          />
                        </Col>
                        <Col span={24}>
                          <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${selectedSupervision.effective_readings} / ${selectedSupervision.read_positions}`}</b>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </>
              ) : (
                <>
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Row gutter={[10, 10]}>
                      <Col span={24} style={{ textAlign: 'center' }} >
                        <Card
                          title={language === "es" ? "General" : "General"}
                          style={{ height: "100%" }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                              }}
                            >
                              <b>{language === "es" ? "Numero de conteo:" : "Counting number:"}</b>
                              <span style={{ fontWeight: 500 }}>{supervisionWarehouse.counting_number}</span>
                            </span>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                              }}
                            >
                              <b>{language === "es" ? "Cantidad de dispositivos:" : "Devices counting:"}</b>
                              <span style={{ fontWeight: 500 }}>{supervisionWarehouse.devices_count}</span>
                            </span>
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={screens.xs ? 24 : 8} style={{ textAlign: 'center' }}>
                    <Card title={language === "es" ? "Lecturas" : "Readings"}>
                      <Row gutter={[10, 15]}>
                        <Col span={23}>
                          <Progress type="line" percent={parseFloat((supervisionWarehouse.reading_percentage * 100).toFixed(2))}
                            format={(percent) => `${percent}%`} strokeWidth={10} status="active" />
                        </Col>
                        <Col span={24}>
                          <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${supervisionWarehouse.read_positions} / ${supervisionWarehouse.total_count}`}</b>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  <Col span={screens.xs ? 24 : 8} style={{ textAlign: 'center' }}>
                    <Card title={language === "es" ? "Efectividad" : "Effective"}>
                      <Row gutter={[10, 15]}>
                        <Col span={23}>
                          <Progress type="line" percent={parseFloat((supervisionWarehouse.effective_percentage * 100).toFixed(2))}
                            format={(percent) => `${percent}%`} strokeWidth={10} status="active" 
                            strokeColor={supervisionWarehouse.effective_percentage > 0.75 ? "#1677ff" : supervisionWarehouse.effective_percentage > 0.50 ? "#fadb14" : "#f5222d"} />
                        </Col>
                        <Col span={24}>
                          <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${supervisionWarehouse.effective_readings} / ${supervisionWarehouse.read_positions}`}</b>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          }

          {/* {devicesPendingCount && screens.lg === true &&
            <Row gutter={[10, 10]}>
              {devicesPendingCount.map((devicePendigCount, i) => (
                <Col key={i} span={8} style={{ textAlign: 'center' }}>
                  <Card title={devicePendigCount.name}
                    extra={<Link to={`logs/${devicePendigCount.idx}`} >
                      <Button
                        type="primary"
                        shape="round"
                        icon={<InfoCircleOutlined />}
                      >
                        {language === "es" ? "Detalles" : "Details"}
                      </Button>
                    </Link>}>
                    <Row gutter={[10, 15]}>
                      <Col span={12}>
                        <Card bordered={false}>
                          <Statistic
                            title={language === "es" ? "Lecturas pendientes en el dispositivo" : "Device pending readings"}
                            value={devicePendigCount.pending_readings_count}
                          />
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card bordered={false}>
                          <Statistic
                            title={language === "es" ? "Imagenes pendientes en el dispositivo" : "Device pending images"}
                            value={devicePendigCount.pending_images_count}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          } */}
        </Card>}
        <Divider >{language === "es" ? "Racks" : "Corridors"}</Divider>
        <Spin spinning={corridorsLoading && !loading}>
          {/* <CorridorsControls onApply={onApplyFilters} devicesNames={devicesPendingCount.map(entry => entry.name)} handleExpandAll={handleExpandAll} handleCollapseAll={handleCollapseAll} />
          <Divider /> */}
          {supervisions && (
        <>
          <CorridorSelectorMap
            corridors={filteredByDeviceAndState}
            selectedCorridorName={selectedCorridorName}
            onSelect={setSelectedCorridorName}
            getCorridorSeverity={getCorridorSeverity}
          />
          {selectedSupervision && selectedCorridorDetails && supervisionWarehouse?.sweep_idx !== undefined && (
            <Spin spinning={corridorMapLoading}>
              <Card
                title={language === "es" ? `Rack ${selectedSupervision.corridor_name}` : `Corridor ${selectedSupervision.corridor_name}`}
                style={{ marginTop: 8, width: "100%" }}
                bodyStyle={corridorDetailsCardBodyStyle}
              >
                <CorridorMap
                  corridorIdx={String(selectedSupervision.corridor_idx)}
                  sweepIdx={String(supervisionWarehouse.sweep_idx)}
                  supervisiondetails={selectedCorridorDetails}
                />
              </Card>
            </Spin>
          )}
          {/* <Row style={{ marginBottom: "16px" }}>
            <Col>
              <Button type="primary" onClick={handleExpandAll}>
                {language === "es" ? "Expandir Todo" : "Expand All"}
              </Button>
              <Button
                type="default"
                onClick={handleCollapseAll}
                style={{ marginLeft: "8px" }}
              >
                {language === "es" ? "Colapsar Todo" : "Collapse All"}
              </Button>
            </Col>
          </Row> */}
          {/* <Row gutter={[16, 16]} style={{ overflow: "auto" }}>
            {filteredSupervisions.map((supervision, i) => (
              (() => {
                const severity = getCorridorSeverity(supervision);
                return (
              <Col
                key={i}
                xs={24}
                sm={24}
                md={12}
                lg={8}
                style={{ overflow: "auto" }}
              >
                <Collapse
                  activeKey={activeKeys}
                  onChange={(keys) => setActiveKeys(keys as string[])}
                >
                  <Panel
                    header={
                      <Row>
                        <Col span={2}>{supervision.corridor_name}</Col>
                        <Col span={22}>
                          {supervision.reading_percentage > 0.1 ? (
                            <Progress
                              showInfo={false}
                              percent={parseFloat(
                                (supervision.reading_percentage * 100).toFixed(2)
                              )}
                              strokeColor={severity.strokeColor}
                            />
                          ) : (
                            <span style={{ marginLeft: "10px", display: "inline-flex", alignItems: "center", minHeight: "22px" }}>
                              <Badge color="#fadb14" />
                            </span>
                          )}
                        </Col>
                      </Row>
                    }
                    extra={
                      <Link
                        to={`${supervisionWarehouse?.sweep_idx}/${supervision.corridor_idx}`}
                      >
                        <Button
                          type="primary"
                          size="small"
                          shape="round"
                          icon={<InfoCircleOutlined />}
                        >
                          {language === "es" ? "Detalles" : "Details"}
                        </Button>
                      </Link>
                    }
                    key={supervision.corridor_name}
                  >
                    {supervision.corridor_idx ? (
                      <CorridorInfo
                        parentWidth={parentSizes[i]?.width || 0}
                        parentHeight={parentSizes[i]?.height || 0}
                        supervision={supervision}
                        loading={loading}
                      />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </Panel>
                </Collapse>
              </Col>
                );
              })()
            ))}
          </Row> */}
        </>
      )}
        </Spin>
      </Spin>
    </Card>)
}

export default Corridor;